import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "space-y-3" }
const _hoisted_2 = { class: "flex gap-6 items-start" }
const _hoisted_3 = {
  key: 0,
  class: "text-center"
}
const _hoisted_4 = ["src"]
const _hoisted_5 = { class: "flex-1 min-h-[112px]" }
const _hoisted_6 = {
  key: 0,
  class: "w-2/4 flex-shrink-0"
}
const _hoisted_7 = { class: "font-medium" }
const _hoisted_8 = { class: "bg-white border border-gray-200 rounded-t-lg py-2 px-3 shadow flex flex-row justify-between items-center" }
const _hoisted_9 = { class: "text-sm font-medium text-gray-900" }
const _hoisted_10 = { class: "bg-white border border-gray-200 rounded-b-lg px-3 py-1 shadow" }
const _hoisted_11 = { class: "flex justify-between my-3 items-end" }
const _hoisted_12 = { class: "flex-col flex-1 min-w-0" }
const _hoisted_13 = { class: "flex items-center" }
const _hoisted_14 = {
  key: 0,
  class: "text-success-800 w-full"
}
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "text-sm font-medium" }
const _hoisted_17 = { class: "flex items-center" }
const _hoisted_18 = { class: "text-gray-900 text-sm mr-2 truncate flex-1" }
const _hoisted_19 = { class: "text-gray-400 text-sm flex-shrink-0" }
const _hoisted_20 = {
  key: 1,
  class: "text-danger-700 w-full"
}
const _hoisted_21 = { class: "flex items-center" }
const _hoisted_22 = { class: "text-sm font-medium" }
const _hoisted_23 = { class: "flex items-center" }
const _hoisted_24 = { class: "text-sm mr-2 truncate flex-1" }
const _hoisted_25 = { class: "text-gray-400 text-sm flex-shrink-0" }
const _hoisted_26 = {
  key: 2,
  class: "text-primary-500 w-full"
}
const _hoisted_27 = { class: "flex items-center mb-1" }
const _hoisted_28 = { class: "text-sm font-medium" }
const _hoisted_29 = { class: "flex items-center" }
const _hoisted_30 = { class: "text-gray-900 text-sm mr-2 truncate flex-1" }
const _hoisted_31 = { class: "text-gray-400 text-sm flex-shrink-0" }
const _hoisted_32 = { class: "flex-shrink-0 ml-3" }

import { computed, onBeforeUnmount, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import vueFilePond from 'vue-filepond';
import {
  FilePondErrorDescription,
  FilePondFile,
  FilePondInitialFile,
  FilePondServerConfigProps,
  FileStatus
} from 'filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import { PRIVATE_LABEL_CONSTANTS } from '@/constants/private-label';
import type { FileAnswer } from '@/models/file.model';
import { apiClient } from '@/services/api';
import cookieService from '@/services/cookie.service';
import BaseIcon from '@/lib/components/Icon/BaseIcon.vue';
import MedKitButton from '@/lib/components/Button/MedKitButton.vue';
import { extractUuid } from '@/helpers/uuid.helper';

// ===== TYPE DEFINITIONS =====
interface Props {
  uploadedFilePreview: string;
  error?: string;
  modelValue?: FileAnswer[];
}

interface UploadingFile {
  id: string;
  originalId: string;
  name: string;
  size: number;
  type: string;
  extension?: string;
  status?: FileStatus;
  progress?: number;
  error?: string;
  isNew?: boolean;
  created_at?: string;
}

// ===== COMPONENT SETUP =====

export default /*@__PURE__*/_defineComponent({
  __name: 'PrivateLabelFileUpload',
  props: {
    uploadedFilePreview: {},
    error: {},
    modelValue: {}
  },
  emits: ["update:modelValue", "uploading"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const { uploadedFilePreview, error, modelValue } = toRefs(props);
const { t } = useI18n();

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

const emit = __emit;

// ===== REACTIVE STATE =====
const uploadedFiles = computed({
  get: () => modelValue.value ?? [],
  set: (value: FileAnswer[]) => {
    emit('update:modelValue', value);
  }
});

const filePond = ref();
const internalFileList = ref<Array<FileAnswer | FilePondFile>>([]);
const files = ref<Array<FilePondFile | FilePondInitialFile>>([]);
const uploadingFiles = ref<Array<UploadingFile>>([]);
const fileToRemove = ref<UploadingFile | null>(null);
const overridePreviewSrc = ref<string>('');
const lastObjectUrl = ref<string>('');
const forcePlaceholder = ref<boolean>(false);

// ===== COMPUTED PROPERTIES =====
const latestUploadedFile = computed<FileAnswer | null>(() => {
  const files = uploadedFiles.value;
  if (!files || files.length === 0) {
    return null;
  }

  const anyHasCreatedAt = files.some((f) => !!f.created_at);
  if (!anyHasCreatedAt) {
    return files[files.length - 1];
  }

  let latest: FileAnswer | null = null;
  let latestTime = -Infinity;

  for (const file of files) {
    const time = file.created_at ? Date.parse(file.created_at) : -Infinity;
    if (time >= latestTime) {
      latest = file;
      latestTime = time;
    }
  }

  return latest;
});

const computedPreviewSrc = computed<string>(() => {
  if (forcePlaceholder.value) {
    return '';
  }
  if (overridePreviewSrc.value) {
    return overridePreviewSrc.value;
  }
  if (latestUploadedFile.value) {
    const idOnly = extractUuid(latestUploadedFile.value.id) || latestUploadedFile.value.id;
    return `${apiClient.defaults.baseURL}/v1/images/${idOnly}`;
  }
  return uploadedFilePreview.value;
});

const previewSize = computed(() => ({
  width: `${PRIVATE_LABEL_CONSTANTS.FILE_UPLOAD.PREVIEW_SIZE.WIDTH}px`,
  height: `${PRIVATE_LABEL_CONSTANTS.FILE_UPLOAD.PREVIEW_SIZE.HEIGHT}px`
}));

const isFileUploading = computed<boolean>(() =>
  uploadingFiles.value.some((file) =>
    file.status === FileStatus.PROCESSING || file.status === FileStatus.PROCESSING_QUEUED
  )
);

// ===== FILEPOND CONFIGURATION =====
const sizeLimitLabel = computed<string>(() => t('platform.file-upload.max-size-note', [
  PRIVATE_LABEL_CONSTANTS.FILE_UPLOAD.SIZE_LIMIT
]));

const labelIdle = computed<string>(() => `
<div class="flex w-full justify-between items-center">
  <div class="text-gray-900 text-sm font-medium">
    ${t('platform.file-upload.drag-drop')}
    <span class="btn btn-small btn-ghost px-3 py-2 rounded shadow cursor-pointer text-gray-800">
      ${t('platform.file-upload.browse')}
    </span>
  </div>
  <div>${sizeLimitLabel.value}</div>
</div>`);

const allowFileTypeValidation = computed<boolean>(() => PRIVATE_LABEL_CONSTANTS.FILE_UPLOAD.EXTENSIONS.trim().length > 0);

const server = computed<FilePondServerConfigProps['server']>(() => ({
  url: `${apiClient.defaults.baseURL}${PRIVATE_LABEL_CONSTANTS.FILE_UPLOAD.SERVER_URL}`,
  headers: {
    [apiClient.defaults.xsrfHeaderName]: cookieService.getXsrfToken()
  },
  process: {
    url: '/',
    ondata: (formData: FormData) => {
      formData.append('options[allow_all_types]', '1');
      return formData;
    },
    onload: (response: string) => {
      try {
        const parsed = JSON.parse(response);
        return parsed?.data?.id ?? response;
      } catch (_) {
        return response;
      }
    }
  },
  revert: (uniqueFileId: string, load: () => void) => {
    load();
  }
}));

const answer = computed<Array<FileAnswer>>(() => internalFileList.value.map((file: FileAnswer | FilePondFile) => {
  if ('serverId' in file) {
    const raw = (file.serverId as unknown) as string;
    let id = raw;
    if (raw && raw.trim().startsWith('{')) {
      try {
        const parsed = JSON.parse(raw);
        id = parsed?.data?.id ?? raw;
      } catch (_) {
        // ignore parse errors; use raw id
      }
    }
    return {
      id,
      originalId: (file as FilePondFile).id,
      name: (file as FilePondFile).filename,
      size: (file as FilePondFile).fileSize,
      type: (file as FilePondFile).fileType,
      extension: (file as FilePondFile).fileExtension,
      status: (file as FilePondFile).status,
      created_at: new Date().toISOString()
    } as unknown as FileAnswer;
  }
  return file as FileAnswer;
}));

// ===== WATCHERS =====
watch(isFileUploading, (prev, curr) => {
  if (prev !== curr) {
    emit('uploading', isFileUploading.value);
  }
});

watch(modelValue, async (newValue) => {
  if (newValue && newValue.length > 0) {
    const latest = newValue[newValue.length - 1];
    let enrichedFile = latest;

    if (!latest.size || latest.size === 0) {
      const info = await fetchFileInfo(latest.id, latest.name);
      if (info) {
        enrichedFile = info;
      }
    }

    const uploadingFile: UploadingFile = {
      id: enrichedFile.id,
      originalId: enrichedFile.originalId,
      name: enrichedFile.name,
      size: enrichedFile.size,
      type: enrichedFile.type,
      extension: enrichedFile.extension,
      status: enrichedFile.status,
      created_at: enrichedFile.created_at
    };

    uploadingFiles.value = [uploadingFile];
    files.value = [{
      source: enrichedFile.id,
      options: {
        type: 'local',
        file: {
          name: enrichedFile.name,
          size: enrichedFile.size,
          type: enrichedFile.type
        }
      }
    }];
  } else {
    uploadingFiles.value = [];
    files.value = [];
  }
}, { immediate: true, deep: true });

// ===== UTILITY FUNCTIONS =====
const preloadImage = (src: string) => new Promise<void>((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve();
  img.onerror = () => reject(new Error('Image failed to load'));
  img.src = src;
});

const cleanupPreviewResources = () => {
  if (lastObjectUrl.value) {
    URL.revokeObjectURL(lastObjectUrl.value);
    lastObjectUrl.value = '';
  }
  overridePreviewSrc.value = '';
  forcePlaceholder.value = true;
};

const setPreviewFromBlob = (processFile: FilePondFile, idOnly: string) => {
  try {
    const blob: Blob | undefined = (processFile as any)?.file;
    if (blob) {
      if (lastObjectUrl.value) {
        URL.revokeObjectURL(lastObjectUrl.value);
      }
      const localUrl = URL.createObjectURL(blob);
      lastObjectUrl.value = localUrl;
      overridePreviewSrc.value = localUrl;
      forcePlaceholder.value = false;
    } else {
      overridePreviewSrc.value = uploadedFilePreview.value || `${apiClient.defaults.baseURL}/v1/images/${idOnly}`;
      forcePlaceholder.value = false;
    }
  } catch (error) {
    overridePreviewSrc.value = uploadedFilePreview.value || `${apiClient.defaults.baseURL}/v1/images/${idOnly}`;
    forcePlaceholder.value = false;
  }
};

const updatePreviewFromServer = async (idOnly: string) => {
  try {
    const info = await fetchFileInfo(idOnly, 'image.png');
    if (info) {
      const serverUrl = `${apiClient.defaults.baseURL}/v1/images/${idOnly}?ts=${Date.now()}`;
      try {
        await preloadImage(serverUrl);
        if (lastObjectUrl.value) {
          URL.revokeObjectURL(lastObjectUrl.value);
          lastObjectUrl.value = '';
        }
        overridePreviewSrc.value = serverUrl;
      } catch (error) {
        // Keep local preview until server becomes available
        console.warn('Failed to load server image:', error);
      }
    }
  } catch (error) {
    console.warn('Failed to fetch file info:', error);
  }
};

const fetchFileInfo = async (fileId: string, filename?: string): Promise<FileAnswer | null> => {
  try {
    const idOnly = extractUuid(fileId) || fileId;
    const response = await apiClient.head(`/v1/images/${idOnly}`);
    const contentLength = response.headers['content-length'];
    const contentType = response.headers['content-type'];

    if (contentLength) {
      return {
        id: idOnly,
        originalId: idOnly,
        name: filename || 'image.png',
        size: parseInt(contentLength),
        type: contentType || 'image/png',
        extension: 'png',
        status: FileStatus.PROCESSING_COMPLETE,
        created_at: new Date().toISOString()
      } as unknown as FileAnswer;
    }
    return null;
  } catch (e) {
    return {
      id: extractUuid(fileId) || fileId,
      originalId: extractUuid(fileId) || fileId,
      name: filename || 'image.png',
      size: 0,
      type: 'image/png',
      extension: 'png',
      status: FileStatus.PROCESSING_COMPLETE,
      created_at: new Date().toISOString()
    } as unknown as FileAnswer;
  }
};

// ===== FILEPOND EVENT HANDLERS =====
const handleAddFile = (err: { main: string; sub: string } | null, file: FilePondFile) => {
  const fileInfo: UploadingFile = {
    id: file.id,
    originalId: file.id,
    name: file.filename,
    size: file.fileSize,
    type: file.fileType,
    extension: file.fileExtension,
    status: file.status,
    progress: 0
  };

  if (err) {
    fileInfo.error = err.main;
  } else {
    fileInfo.isNew = true;
  }

  uploadingFiles.value = [fileInfo];
};

const handleProcessFileStart = (file: FilePondFile) => {
  const target = uploadingFiles.value.find((f) => f.originalId === file.id);
  if (target) {
    target.status = file.status;
  }
};

const handleProcessFileProgress = (processingFile: FilePondFile, progress: number) => {
  const target = uploadingFiles.value.find((f) => f.originalId === processingFile.id);
  if (target) {
    target.progress = progress;
  }
};
const errorMessage = ref('');
const handleProcessFile = async (err: FilePondErrorDescription | null, processFile: FilePondFile) => {
  if (err) {
    if (typeof err.code === 'number') {
      if (err.code === 403) {
        errorMessage.value = t('platform.file-upload.error-forbidden');
      } else if (err.code === 400) {
        errorMessage.value = t('platform.file-upload.error-invalid-format');
      } else {
        errorMessage.value = t('platform.file-upload.general-error-message');
      }
    } else if (err.body && typeof err.body === 'string') {
      try {
        const parsed = JSON.parse(err.body);
        if (parsed?.error?.message) {
          errorMessage.value = parsed.error.message;
        }
      } catch {
        if (/\b403\b/.test(err.body)) {
          errorMessage.value = t('platform.file-upload.error-forbidden');
        } else if (/\b400\b/.test(err.body)) {
          errorMessage.value = t('platform.file-upload.error-invalid-format');
        } else {
          errorMessage.value = t('platform.file-upload.general-error-message');
        }
      }
    } else {
      errorMessage.value = t('platform.file-upload.general-error-message');
    }
    uploadingFiles.value = uploadingFiles.value.map((f) =>
      f.originalId === processFile.id
        ? { ...f, status: processFile.status, error: errorMessage.value }
        : f
    );
  } else {
    internalFileList.value = [processFile];
    uploadingFiles.value = uploadingFiles.value.map((f) => (
      f.originalId === processFile.id
        ? {
          ...f,
          status: processFile.status,
          id: (processFile.serverId as unknown as string),
          name: processFile.filename,
          isNew: false
        }
        : f
    ));

    try {
      const newIdRaw = (processFile.serverId as unknown as string);
      const idOnly = extractUuid(newIdRaw) || newIdRaw;

      // Show local preview immediately while backend object becomes available
      setPreviewFromBlob(processFile, idOnly);

      // Verify existence and then preload & swap to server URL (sanitized) without flicker
      await updatePreviewFromServer(idOnly);
    } catch (error) {
      console.warn('Failed to process file upload:', error);
    }

    // Emit updated model immediately on successful process
    emit('update:modelValue', answer.value);
  }
};

const handleRemoveFile = (err: FilePondErrorDescription | null, file: FilePondFile) => {
  if (!err) {
    internalFileList.value = internalFileList.value.filter((f) =>
      ('serverId' in f ? (f as FilePondFile).filename : (f as FileAnswer).name) !== file.filename
    );
    uploadingFiles.value = uploadingFiles.value.filter((f) => f.name !== file.filename);
    emit('update:modelValue', answer.value);

    if (uploadingFiles.value.length === 0) {
      cleanupPreviewResources();
    }
  }
};

const saveAnswer = () => {
  emit('update:modelValue', answer.value);
};

// ===== FILE MANAGEMENT FUNCTIONS =====
const findFileIndexFromInstance = (targetFile: UploadingFile): number => {
  if (!filePond.value) {
    return -1;
  }

  const allFiles = filePond.value.getFiles();

  if (targetFile.isNew === true) {
    return allFiles.findIndex((f: FilePondFile) => targetFile.originalId === f.id);
  }

  return allFiles.findIndex((f: FilePondFile) => {
    const serverIdRaw = f.serverId as unknown as string;
    if (!serverIdRaw) {
      return false;
    }

    try {
      const parsed = JSON.parse(serverIdRaw);
      const sid = parsed.data?.id;
      if (sid) {
        return sid === targetFile.id;
      }
    } catch (error) {
      // Ignore parse error and fall through to direct comparison
    }

    return serverIdRaw === targetFile.id;
  });
};

const removeFileManually = (targetFile: UploadingFile) => {
  try {
    if (filePond.value) {
      const allFiles = filePond.value.getFiles();
      const index = findFileIndexFromInstance(targetFile);
      if (index > -1) {
        const instance = allFiles[index];
        filePond.value.removeFile(instance, { revert: false });
        const uploadingIndex = uploadingFiles.value.findIndex((f) => f.id === targetFile.id);
        if (uploadingIndex > -1) {
          uploadingFiles.value.splice(uploadingIndex, 1);
        }
      }
    }
  } finally {
    fileToRemove.value = null;
  }
};

const dismissFile = (targetFile: UploadingFile) => {
  uploadingFiles.value = uploadingFiles.value.filter((f) => f.id !== targetFile.id);
};

const cancelFile = (targetFile: UploadingFile) => {
  if (filePond.value) {
    const allFiles = filePond.value.getFiles();
    const index = findFileIndexFromInstance(targetFile);

    if (index > -1) {
      const fileInstance = allFiles[index];

      // First try to abort processing if it's in progress
      if (fileInstance.status === FileStatus.PROCESSING ||
          fileInstance.status === FileStatus.PROCESSING_QUEUED) {
        fileInstance.abortProcessing();
      }

      // Then remove the file from FilePond
      filePond.value.removeFile(fileInstance, { revert: true });

      // Also remove from our internal state
      const uploadingIndex = uploadingFiles.value.findIndex((f) => f.id === targetFile.id);
      if (uploadingIndex > -1) {
        uploadingFiles.value.splice(uploadingIndex, 1);
      }
    }
  }
};

// ===== UTILITY FUNCTIONS =====
const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) {
    return '0 Bytes';
  }
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const getProgress = (progress: string) => {
  const p = parseFloat(progress);
  const val = isNaN(p) ? 0 : p;
  const clamped = Math.min(Math.max(val, 0), 1);
  return (clamped * 100).toFixed(2);
};

const handleImgError = () => {
  // Be conservative: if current src is local (blob:), do nothing to avoid loops
  if (overridePreviewSrc.value && overridePreviewSrc.value.startsWith('blob:')) {
    return;
  }
  // Fallback to last known local preview or provided uploadedFilePreview, else placeholder
  if (lastObjectUrl.value) {
    overridePreviewSrc.value = lastObjectUrl.value;
    forcePlaceholder.value = false;
    return;
  }
  if (uploadedFilePreview.value) {
    overridePreviewSrc.value = uploadedFilePreview.value;
    forcePlaceholder.value = false;
    return;
  }
  overridePreviewSrc.value = '';
  forcePlaceholder.value = true;
};

// ===== LIFECYCLE =====
onBeforeUnmount(() => {
  if (lastObjectUrl.value) {
    URL.revokeObjectURL(lastObjectUrl.value);
  }
});
__expose({
  handleProcessFile,
  errorMessage
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[1] || (_cache[1] = _createElementVNode("div", { class: "text-base font-medium text-gray-900" }, " Packshot ", -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        class: "border border-gray-300 rounded flex items-center justify-center text-sm text-gray-500 flex-shrink-0 p-2",
        style: _normalizeStyle(previewSize.value),
        "data-dusk": "private-label-image-preview"
      }, [
        (!computedPreviewSrc.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(_unref(PRIVATE_LABEL_CONSTANTS).FORM.PREVIEW_PLACEHOLDER), 1))
          : (_openBlock(), _createElementBlock("img", {
              key: 1,
              src: computedPreviewSrc.value,
              alt: "Packshot preview",
              class: "w-full h-full object-cover rounded",
              onError: handleImgError
            }, null, 40, _hoisted_4))
      ], 4),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", {
          class: _normalizeClass(["pr-1", _unref(error) ? 'border border-danger-700' : ''])
        }, [
          _createElementVNode("input", {
            type: "file",
            "data-dusk": "filepond-input",
            class: "hidden",
            onChange: _cache[0] || (_cache[0] = ($event) => {
              const f = ($event.target as HTMLInputElement).files?.[0];

              if (f) {
                if (f && filePond.value?.value?.addFile) { (filePond.value.value as any).addFile(f); }
              } else {
                filePond.value.value?.removeFile();
              }
            })
          }, null, 32),
          _createVNode(_unref(FilePond), {
            ref_key: "filePond",
            ref: filePond,
            name: "file",
            "data-dusk": "filepond",
            "max-parallel-uploads": 10000,
            "label-idle": labelIdle.value,
            "label-invalid-field": _ctx.$t('platform.file-upload.invalid-field'),
            "label-file-waiting-for-size": _ctx.$t('platform.file-upload.file-waiting-for-size'),
            "label-file-size-not-available": _ctx.$t('platform.file-upload.file-size-not-available'),
            "label-file-loading": _ctx.$t('platform.file-upload.file-loading'),
            "label-file-load-error": _ctx.$t('platform.file-upload.file-load-error'),
            "label-file-processing": _ctx.$t('platform.file-upload.file-processing'),
            "label-file-processing-complete": _ctx.$t('platform.file-upload.file-processing-complete'),
            "label-file-processing-aborted": _ctx.$t('platform.file-upload.file-processing-aborted'),
            "label-file-processing-error": _ctx.$t('platform.file-upload.file-processing-error'),
            "label-file-processing-revert-error": _ctx.$t('platform.file-upload.file-processing-revert-error'),
            "label-file-remove-error": _ctx.$t('platform.file-upload.file-remove-error'),
            "label-tap-to-cancel": _ctx.$t('platform.file-upload.tap-to-cancel'),
            "label-tap-to-retry": _ctx.$t('platform.file-upload.tap-to-retry'),
            "label-tap-to-undo": _ctx.$t('platform.file-upload.tap-to-undo'),
            "label-button-remove-item": _ctx.$t('platform.file-upload.button-remove-item'),
            "label-button-abort-item-load": _ctx.$t('platform.file-upload.button-abort-item-load'),
            "label-button-retry-item-load": _ctx.$t('platform.file-upload.button-retry-item-load'),
            "label-button-abort-item-processing": _ctx.$t('platform.file-upload.button-abort-item-processing'),
            "label-button-undo-item-processing": _ctx.$t('platform.file-upload.button-undo-item-processing'),
            "label-button-retry-item-processing": _ctx.$t('platform.file-upload.button-retry-item-processing'),
            "label-button-process-item": _ctx.$t('platform.file-upload.button-process-item'),
            "label-max-file-size-exceeded": _ctx.$t('platform.file-upload.max-file-size-exceeded'),
            "label-max-file-size": _ctx.$t('platform.file-upload.max-file-size'),
            "label-max-total-file-size": _ctx.$t('platform.file-upload.max-total-file-size'),
            "label-file-type-not-allowed": _ctx.$t('platform.file-upload.file-type-not-allowed'),
            "file-validate-type-label-expected-types": _ctx.$t('platform.file-upload.file-validate-type-label-expected-types'),
            class: "border-dashed border border-gray-300 rounded h-full",
            "allow-file-type-validation": allowFileTypeValidation.value,
            "accepted-file-types": _unref(PRIVATE_LABEL_CONSTANTS).FILE_UPLOAD.EXTENSIONS,
            server: server.value,
            "allow-multiple": "false",
            files: files.value,
            "max-file-size": _unref(PRIVATE_LABEL_CONSTANTS).FILE_UPLOAD.SIZE_LIMIT,
            onAddfile: handleAddFile,
            onProcessfile: handleProcessFile,
            onRemovefile: handleRemoveFile,
            onProcessfiles: saveAnswer,
            onProcessfilestart: handleProcessFileStart,
            onProcessfileprogress: handleProcessFileProgress
          }, null, 8, ["label-idle", "label-invalid-field", "label-file-waiting-for-size", "label-file-size-not-available", "label-file-loading", "label-file-load-error", "label-file-processing", "label-file-processing-complete", "label-file-processing-aborted", "label-file-processing-error", "label-file-processing-revert-error", "label-file-remove-error", "label-tap-to-cancel", "label-tap-to-retry", "label-tap-to-undo", "label-button-remove-item", "label-button-abort-item-load", "label-button-retry-item-load", "label-button-abort-item-processing", "label-button-undo-item-processing", "label-button-retry-item-processing", "label-button-process-item", "label-max-file-size-exceeded", "label-max-file-size", "label-max-total-file-size", "label-file-type-not-allowed", "file-validate-type-label-expected-types", "allow-file-type-validation", "accepted-file-types", "server", "files", "max-file-size"])
        ], 2)
      ]),
      (uploadingFiles.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('platform.file-upload.files', { count: uploadingFiles.value.length })), 1)
              ]),
              _createElementVNode("div", _hoisted_10, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(uploadingFiles.value, (file, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: _normalizeClass([uploadingFiles.value.length !== index + 1 ? 'border-b border-gray-100' : ''])
                  }, [
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("div", _hoisted_12, [
                        _createElementVNode("div", _hoisted_13, [
                          (file.status === _unref(FileStatus).PROCESSING_COMPLETE || !('status' in file))
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                _createElementVNode("div", _hoisted_15, [
                                  _createVNode(BaseIcon, {
                                    name: "check",
                                    class: "mr-2 text-success-600"
                                  }),
                                  _createElementVNode("span", _hoisted_16, _toDisplayString(_ctx.$t('platform.file-upload.uploaded')), 1)
                                ]),
                                _createElementVNode("div", _hoisted_17, [
                                  _createElementVNode("div", _hoisted_18, _toDisplayString(file.name), 1),
                                  _createElementVNode("div", _hoisted_19, _toDisplayString(formatBytes(file.size)), 1)
                                ])
                              ]))
                            : (file.error)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                  _createElementVNode("div", _hoisted_21, [
                                    _createVNode(BaseIcon, {
                                      name: "warning",
                                      class: "mr-2 text-danger-600",
                                      height: "1rem"
                                    }),
                                    _createElementVNode("span", _hoisted_22, _toDisplayString(file.error), 1)
                                  ]),
                                  _createElementVNode("div", _hoisted_23, [
                                    _createElementVNode("div", _hoisted_24, _toDisplayString(file.name), 1),
                                    _createElementVNode("div", _hoisted_25, _toDisplayString(formatBytes(file.size)), 1)
                                  ])
                                ]))
                              : (_openBlock(), _createElementBlock("div", _hoisted_26, [
                                  _createElementVNode("div", _hoisted_27, [
                                    _createVNode(BaseIcon, {
                                      name: "loader",
                                      class: "mr-2 text-primary-600 animate-spin"
                                    }),
                                    _createElementVNode("span", _hoisted_28, _toDisplayString(_ctx.$t('platform.file-upload.file-processing')) + " - " + _toDisplayString(getProgress((file.progress ?? 0).toString())) + "%", 1)
                                  ]),
                                  _createElementVNode("div", _hoisted_29, [
                                    _createElementVNode("div", _hoisted_30, _toDisplayString(file.name), 1),
                                    _createElementVNode("div", _hoisted_31, _toDisplayString(formatBytes(file.size)), 1)
                                  ])
                                ]))
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_32, [
                        (file.status === _unref(FileStatus).PROCESSING || file.status === _unref(FileStatus).PROCESSING_QUEUED)
                          ? (_openBlock(), _createBlock(MedKitButton, {
                              key: 0,
                              label: _ctx.$t('platform.file-upload.button-abort-item-processing'),
                              kind: "secondary",
                              size: "small",
                              onClick: ($event: any) => (cancelFile(file))
                            }, null, 8, ["label", "onClick"]))
                          : _createCommentVNode("", true),
                        ((file.status === _unref(FileStatus).PROCESSING_COMPLETE || !('status' in file)) && !isFileUploading.value)
                          ? (_openBlock(), _createBlock(MedKitButton, {
                              key: 1,
                              label: _ctx.$t('platform.file-upload.button-remove-item'),
                              kind: "tertiary",
                              size: "small",
                              onClick: ($event: any) => (removeFileManually(file))
                            }, null, 8, ["label", "onClick"]))
                          : _createCommentVNode("", true),
                        (file.error)
                          ? (_openBlock(), _createBlock(MedKitButton, {
                              key: 2,
                              label: _ctx.$t('platform.common.dismiss'),
                              kind: "secondary",
                              size: "small",
                              onClick: ($event: any) => (dismissFile(file))
                            }, null, 8, ["label", "onClick"]))
                          : _createCommentVNode("", true)
                      ])
                    ])
                  ], 2))
                }), 128))
              ])
            ])
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})